import React, { useState, useEffect } from 'react';
import { CheckIcon, XMarkIcon, ClockIcon, EnvelopeIcon, ShieldCheckIcon, ExclamationCircleIcon, FolderIcon } from '@heroicons/react/24/outline';
import { CollectionSharingService } from '../../services/api';
import { toast } from 'react-hot-toast';

interface Invitation {
  id: string;
  collection_id: string;
  collection_name: string;
  inviter_name: string;
  permission: 'read' | 'write' | 'admin';
  status: 'pending' | 'accepted' | 'rejected' | 'expired';
  expires_at: string;
  created_at: string;
}

interface CollectionInvitationsProps {
  onInvitationAccepted?: () => void;
  className?: string;
}

export const CollectionInvitations: React.FC<CollectionInvitationsProps> = ({
  onInvitationAccepted,
  className = ''
}) => {
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);

  const fetchInvitations = async () => {
    try {
      const response = await CollectionSharingService.listUserInvitations({
        status: 'pending'
      });
      const safeInvitations = Array.isArray(response)
        ? response.map((inv: unknown) => {
            const r: Record<string, unknown> = (inv && typeof inv === 'object') ? (inv as Record<string, unknown>) : {}
            const getString = (v: unknown): string => typeof v === 'string' ? v : ''
            const isPermission = (v: unknown): v is 'read' | 'write' | 'admin' => typeof v === 'string' && ['read','write','admin'].includes(v)
            const isStatus = (v: unknown): v is 'pending' | 'accepted' | 'rejected' | 'expired' => typeof v === 'string' && ['pending','accepted','rejected','expired'].includes(v)
            return {
              id: getString(r.id),
              collection_id: getString(r.collection_id),
              collection_name: getString(r.collection_name),
              inviter_name: getString(r.inviter_name),
              permission: isPermission(r.permission) ? r.permission : 'read',
              status: isStatus(r.status) ? r.status : 'pending',
              expires_at: getString(r.expires_at) || new Date().toISOString(),
              created_at: getString(r.created_at) || new Date().toISOString(),
            } as Invitation
          })
        : []
      setInvitations(safeInvitations);
    } catch (error) {
      console.error('Failed to fetch invitations:', error);
      toast.error('Failed to load invitations');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInvitations();
  }, []);

  const handleAccept = async (invitationToken: string) => {
    setProcessingId(invitationToken);
    try {
      await CollectionSharingService.acceptCollectionInvitation({
        requestBody: {
          invitation_token: invitationToken
        }
      });
      
      toast.success('Invitation accepted successfully');
      setInvitations(prev => prev.filter(inv => inv.id !== invitationToken));
      onInvitationAccepted?.();
    } catch (error: unknown) {
      let message = 'Failed to accept invitation';
      if (error && typeof error === 'object') {
        const { body, message: errorMessage } = error as { body?: unknown; message?: unknown };
        if (body && typeof body === 'object' && 'message' in body) {
          const maybeMsg = (body as { message?: unknown }).message
          if (typeof maybeMsg === 'string') message = maybeMsg;
        } else if (typeof errorMessage === 'string') {
          message = errorMessage;
        }
      }
      toast.error(message);
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (invitationToken: string) => {
    setProcessingId(invitationToken);
    try {
      await CollectionSharingService.rejectCollectionInvitation({
        invitationToken,
        requestBody: {}
      });
      
      toast.success('Invitation rejected');
      setInvitations(prev => prev.filter(inv => inv.id !== invitationToken));
    } catch (error: unknown) {
      let message = 'Failed to reject invitation';
      if (error && typeof error === 'object') {
        const { body, message: errorMessage } = error as { body?: unknown; message?: unknown };
        if (body && typeof body === 'object' && 'message' in body) {
          const maybeMsg = (body as { message?: unknown }).message
          if (typeof maybeMsg === 'string') message = maybeMsg;
        } else if (typeof errorMessage === 'string') {
          message = errorMessage;
        }
      }
      toast.error(message);
    } finally {
      setProcessingId(null);
    }
  };

  const getPermissionBadge = (permission: string) => {
    const badges = {
      read: { label: 'View Only', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
      write: { label: 'Editor', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
      admin: { label: 'Admin', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' }
    };
    const isValidPermission = (val: string): val is keyof typeof badges => ['read','write','admin'].includes(val)
    const key = isValidPermission(permission) ? permission : 'read'
    const badge = badges[key];
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        <ShieldCheckIcon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  const getTimeRemaining = (expiresAt: string) => {
    const expirationDate = new Date(expiresAt);
    const now = new Date();
    const diff = expirationDate.getTime() - now.getTime();
    
    if (diff <= 0) return 'Expired';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `${days} day${days > 1 ? 's' : ''} remaining`;
    return `${hours} hour${hours > 1 ? 's' : ''} remaining`;
  };

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center p-8 ${className}`}>
        <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (invitations.length === 0) {
    return (
      <div className={`text-center p-8 ${className}`}>
        <EnvelopeIcon className="w-12 h-12 text-gray-400 dark:text-gray-600 mx-auto mb-3" />
        <p className="text-gray-500 dark:text-gray-400">No pending invitations</p>
      </div>
    );
  }

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="flex items-center gap-2 mb-4">
        <ExclamationCircleIcon className="w-5 h-5 text-yellow-500" />
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Pending Collection Invitations ({invitations.length})
        </h3>
      </div>
      
      {invitations.map((invitation) => (
        <div
          key={invitation.id}
          className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-800 hover:shadow-md transition-shadow"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <FolderIcon className="w-5 h-5 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                <h4 className="font-medium text-gray-900 dark:text-white truncate">
                  {invitation.collection_name}
                </h4>
                {getPermissionBadge(invitation.permission)}
              </div>
              
              <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                <p>
                  Invited by <span className="font-medium">{invitation.inviter_name}</span>
                </p>
                <div className="flex items-center gap-1 text-xs">
                  <ClockIcon className="w-3 h-3" />
                  <span>{getTimeRemaining(invitation.expires_at)}</span>
                </div>
              </div>
            </div>
            
            <div className="flex gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={() => { void handleAccept(invitation.id) }}
                disabled={processingId === invitation.id}
                className="p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors disabled:opacity-50"
                title="Accept invitation"
              >
                {processingId === invitation.id ? (
                  <div className="w-5 h-5 border-2 border-green-600 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <CheckIcon className="w-5 h-5" />
                )}
              </button>
              <button
                type="button"
                onClick={() => { void handleReject(invitation.id) }}
                disabled={processingId === invitation.id}
                className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                title="Reject invitation"
              >
                {processingId === invitation.id ? (
                  <div className="w-5 h-5 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <XMarkIcon className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};